// src/components/home/useHomePrivate.js
//
// State + autosave for the home page's private cards (My notes, My quick
// links; UI revamp). One shared document, so both cards use one instance.
//   - Saves ~600 ms after the last change.
//   - Status: "saving" -> "saved"; a failed save shows "retrying" and retries
//     with growing waits (3 s, 6 s, 12 s, then every 30 s) until it works.
//   - A refusal from the server (400: something broke a rule the page should
//     already have stopped) shows "error" with the reason; the text stays on
//     screen and the next change tries again.
//   - Leaving the page with an unsaved change sends one last save.

import { useCallback, useEffect, useRef, useState } from "react";
import { loadHomePrivate, saveHomePrivate } from "../../services/homePrivateService";

export const SAVE_DELAY_MS = 600;
const RETRY_DELAYS_MS = [3000, 6000, 12000, 30000];

export default function useHomePrivate({ tenantId, siteId, uid }) {
  const [data, setData] = useState({ today: [], week: [], notes: "", links: [], pins: [] });
  const [loaded, setLoaded] = useState(false);
  const [loadError, setLoadError] = useState("");
  const [status, setStatus] = useState("idle");
  const [errorText, setErrorText] = useState("");

  const latest = useRef(data);
  const dirty = useRef(false);
  const saving = useRef(false);
  const timer = useRef(null);
  const attempt = useRef(0);
  const site = useRef({ tenantId, siteId });
  site.current = { tenantId, siteId };

  useEffect(() => {
    if (!uid || !tenantId || !siteId) return undefined;
    let cancelled = false;
    setLoaded(false);
    loadHomePrivate({ tenantId, siteId, uid })
      .then((d) => {
        if (cancelled) return;
        latest.current = d;
        setData(d);
        setLoaded(true);
      })
      .catch((e) => {
        console.error("Couldn't load My notes:", e);
        if (!cancelled) setLoadError("Couldn't load your notes and links right now.");
      });
    return () => {
      cancelled = true;
    };
  }, [uid, tenantId, siteId]);

  const runSave = useCallback(async () => {
    clearTimeout(timer.current);
    if (saving.current || !dirty.current) return;
    saving.current = true;
    dirty.current = false;
    setStatus("saving");
    try {
      await saveHomePrivate({ ...site.current, data: latest.current });
      attempt.current = 0;
      setErrorText("");
      setStatus("saved");
    } catch (e) {
      dirty.current = true; // still not saved
      if (e && e.retryable === false) {
        setErrorText(e.message || "Couldn't save.");
        setStatus("error");
      } else {
        const wait = RETRY_DELAYS_MS[Math.min(attempt.current, RETRY_DELAYS_MS.length - 1)];
        attempt.current += 1;
        setStatus("retrying");
        timer.current = setTimeout(() => runSave(), wait);
      }
    } finally {
      saving.current = false;
      // Changes made while this save was in flight: save them too.
      if (dirty.current && !timer.current) timer.current = setTimeout(() => runSave(), SAVE_DELAY_MS);
    }
  }, []);

  /** Change the data (fn: prev -> next) and schedule a save. */
  const update = useCallback(
    (fn) => {
      const next = fn(latest.current);
      latest.current = next;
      setData(next);
      dirty.current = true;
      attempt.current = 0;
      clearTimeout(timer.current);
      timer.current = setTimeout(() => {
        timer.current = null;
        runSave();
      }, SAVE_DELAY_MS);
    },
    [runSave]
  );

  // Leaving the page: one last save of anything unsaved.
  useEffect(
    () => () => {
      clearTimeout(timer.current);
      if (dirty.current && !saving.current) {
        saveHomePrivate({ ...site.current, data: latest.current }).catch(() => {});
      }
    },
    []
  );

  return { data, loaded, loadError, status, errorText, update };
}
